import { Component, OnInit, Input } from '@angular/core';
import { ProfileService } from 'src/app/core/service/profile.service';
import { SiteMetadataService } from 'src/app/core/service/site-metadata.service';
import { ImagekitUrlAppenderPipe } from 'src/app/shared/pipes/imagekit-url-appender.pipe';
import { toPng } from 'html-to-image';
import { ICertificate } from 'src/app/modules/account/pages/acc-certificates/acc-certificates.component';
import { jsPDF } from 'jspdf';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * `html-to-image` clones the target node's COMPUTED style onto a detached
 * copy, then renders that copy inside an SVG `<foreignObject>` sized to
 * exactly the node's own width/height. The certificate is centred in its
 * page via `margin: 0 auto` (every theme's `.certificate-view` — including
 * `tripura`'s portrait layout, where this was first reported, 2026-09-14:
 * "download as image ... 1st image so fix it properly"), so the clone
 * inherits a RESOLVED PIXEL margin from the original page's layout (e.g.
 * `margin-left: 210px` if the page happened to leave 210px either side) —
 * not the literal keyword `auto`. Dropped into a foreignObject that is only
 * as wide as the certificate itself, that inherited margin pushes the whole
 * certificate sideways by that many pixels and clips it, while leaving a
 * matching blank gap on the opposite side — reproduced and confirmed with a
 * minimal, unrelated test box (no aspect-ratio, no container queries): the
 * `margin: 0 auto` alone was enough to trigger it, so the fix generalises to
 * every theme, not just this portrait one.
 * `style: { margin: '0' }` overrides the clone's own inline style AFTER
 * `html-to-image` finishes copying computed styles (see `apply-style.ts`),
 * so it reliably wins regardless of the resolved margin value. Exported at
 * module scope so both download paths below use the identical fix.
 */
const TOPNG_OPTIONS = { style: { margin: '0' } };

/** Reads a PNG data URL's real pixel dimensions via a throwaway `Image`,
 * since `jsPDF.getImageProperties()` only reports the SOURCE image's size
 * after a `jsPDF` instance already exists — and constructing that instance
 * is exactly the step whose orientation ('landscape'/'portrait') depends on
 * this same aspect ratio (see `downloadCerti()`). */
function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

@Component({
  selector: 'app-certificate-viewer-modal',
  templateUrl: './certificate-viewer-modal.component.html',
  styleUrls: ['./certificate-viewer-modal.component.scss']
})
export class CertificateViewerModalComponent implements OnInit {
 @Input() enrollId: number;
 /* Render a specific cert by its public number (skips the generic fetch).
    Used by per-membership Download buttons that already know which cert to show. */
 @Input() certificateNumber?: string;
 /* Tells the fetch which kind of cert to ask the backend for. Only used when
    there's no enrollId / certificateNumber (i.e. the generic fetchCertificates
    path). Default 'profile' keeps existing callers' behavior. */
 @Input() certificateType: 'profile' | 'course' | 'membership' = 'profile';
  certificates: ICertificate[] = []
  constructor(
    private profileService: ProfileService,
    private imagekit: ImagekitUrlAppenderPipe,
    private siteMetadataService: SiteMetadataService,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    this.siteMetadataService.setTitle('Certificate');
    if(this.certificateNumber){
      this.certificates = [{ number: this.certificateNumber } as ICertificate];
    } else if(this.enrollId){
      this.coursesCertificates();
    } else {
      this.fetchCertificates()
    }
  }
  

   coursesCertificates() {
    this.profileService.courseCertificates(this.enrollId).subscribe((res) => {
      if (res) {
        this.certificates = res.data || []
      }
    })
  }
  fetchCertificates() {
    // SC-SAAS-FRONTEND-EG — the service already toasts on failure and rethrows;
    // a no-op error callback stops that reaching Sentry as an unhandled failure.
    this.profileService.getCertificates(this.certificateType).subscribe({
      next: (res) => {
        if (res) {
          this.certificates = res.data || []
        }
      },
      error: () => {}
    })
  }


  /* The certificate is rendered inside a same-origin <iframe> (the full SPA).
     html-to-image draws the iframe body inside an SVG <foreignObject>, where
     scripting counts as disabled — so the UA rule that hides <noscript> no
     longer applies and index.html's "Please enable JavaScript…" fallback bleeds
     into the bottom of the captured image. The library does NOT forward the
     `filter` option into iframe clones, so we strip the <noscript> from the
     live iframe document right before capturing. This is invisible/harmless on
     a JS-enabled iframe and leaves the real no-JS fallback in index.html intact. */
  private stripCaptureArtifacts(el: HTMLElement | null): void {
    try {
      const doc = (el as HTMLIFrameElement | null)?.contentDocument;
      if (!doc) { return; }
      doc.querySelectorAll('noscript').forEach((n) => n.parentNode?.removeChild(n));
    } catch (e) {
      /* cross-origin or iframe not ready — nothing to strip */
    }
  }

  async downloadCerti() {
    const iframeEl = document.getElementById('certificate-div') as HTMLIFrameElement;
    this.stripCaptureArtifacts(iframeEl);
    const divEle: HTMLElement = iframeEl?.contentDocument?.getElementById('cerificate-view') || iframeEl;
    toPng(divEle, TOPNG_OPTIONS).then(function (dataUrl) {
      return getImageDimensions(dataUrl).then(function (size) {
        /* Orientation was hardcoded to 'landscape' — correct for the original
           six themes (all landscape), but the portrait tripura theme's PDF
           page was then shorter than the certificate itself, so scaling the
           image to the page WIDTH (below) pushed its height past the page's
           bottom edge, cutting the certificate off (2026-09-14, reported
           against a real Octanet PDF download: the last visible line,
           "This Certificate shall only be valid...", sat right at the page
           edge with everything below it missing). Picking the orientation
           from the captured image's own aspect ratio keeps this correct for
           both shapes without special-casing any one theme. */
        const orientation = size.width >= size.height ? 'landscape' : 'portrait';
        const pdf = new jsPDF(orientation);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (size.height * pdfWidth) / size.width;

        pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('my-certificate.pdf');
      });
    })
    .catch(function (error) {
      console.error('oops, something went wrong!', error);
    });
  }


  async downloadCertiAsImage() {
    const iframeEl = document.getElementById('certificate-div') as HTMLIFrameElement;
    this.stripCaptureArtifacts(iframeEl);
    const divEle: HTMLElement = iframeEl?.contentDocument?.getElementById('cerificate-view') || iframeEl;
    toPng(divEle, TOPNG_OPTIONS) .then(function (dataUrl) {
      const link = document.createElement('a')
      link.download = 'my-certificate.png'
      link.href = dataUrl
      link.click()
    })
    .catch(function (error) {
      console.error('oops, something went wrong!', error);
    });
  }

  saveAs(uri, filename) {

    var link = document.createElement('a');

    if (typeof link.download === 'string') {

        link.href = uri;
        link.download = filename;

        //Firefox requires the link to be in the body
        document.body.appendChild(link);

        //simulate click
        link.click();

        //remove the link when done
        document.body.removeChild(link);

    } else {

        window.open(uri);

    }
}

closeModal(): void {
  this.activeModal.close();
}


}
